/**
 * RoleTemplateLibraryPanel - Role Template Library UI
 *
 * FERP CRITICAL: Template discovery and instantiation
 *
 * Features:
 * - Browse template library by category
 * - Search and filter templates
 * - Preview template details
 * - Instantiate templates as roles
 * - Track template usage
 * - Category-based organization
 * - Compliance framework filtering
 *
 * Architecture Principles:
 * - Data-driven (all templates from RoleTemplateRepository)
 * - Backend ID generation (Snowflake IDs)
 * - Repository pattern for operations
 * - Immutable audit trails
 * - Multi-tenant isolation
 *
 * GitHub Issue: #337
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  Stack,
  Group,
  Text,
  Badge,
  Button,
  TextInput,
  Select,
  MultiSelect,
  Grid,
  ScrollArea,
  Alert,
  ActionIcon,
  Tooltip,
  Modal,
  Code,
  Divider,
  Paper,
  Title,
  Loader,
  Center,
} from '@mantine/core';
import {
  IconTemplate,
  IconSearch,
  IconFilter,
  IconRocket,
  IconEye,
  IconCheck,
  IconAlertCircle,
  IconShield,
  IconTrophy,
  IconUsers,
  IconRefresh,
} from '@tabler/icons-react';
import { useApolloClient } from '@apollo/client/react';
import { ApolloClient } from '@apollo/client';
import {
  RoleTemplate,
  RoleTemplateRepository,
  createRoleTemplateRepository,
  InstantiateTemplateInput,
} from '../data/repositories/RoleTemplateRepository';

export interface RoleTemplateLibraryPanelProps {
  /** Tenant ID for multi-tenant isolation */
  tenantId: string;
  /** Callback when template is instantiated */
  onTemplateInstantiate?: (roleId: string) => void;
}

/**
 * Role Template Library Panel
 *
 * Template discovery and instantiation interface
 *
 * FERP Architecture Compliance:
 * - ✅ Data-driven (templates from RoleTemplateRepository)
 * - ✅ Backend Snowflake ID generation
 * - ✅ Repository pattern
 * - ✅ Immutable audit trails
 * - ✅ Multi-tenant isolation
 */
export function RoleTemplateLibraryPanel({
  tenantId,
  onTemplateInstantiate,
}: RoleTemplateLibraryPanelProps) {
  const apolloClient = useApolloClient() as ApolloClient;
  const repository = useMemo(
    () => createRoleTemplateRepository(apolloClient),
    [apolloClient]
  );

  // State
  const [templates, setTemplates] = useState<RoleTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Search and filter
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedFrameworks, setSelectedFrameworks] = useState<string[]>([]);

  // Preview modal
  const [previewTemplate, setPreviewTemplate] = useState<RoleTemplate | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Instantiation
  const [instantiating, setInstantiating] = useState<string | null>(null);
  const [roleNameOverride, setRoleNameOverride] = useState('');

  // Load templates
  useEffect(() => {
    loadTemplates();
  }, [tenantId]);

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await repository.listTemplates(tenantId);
      setTemplates(result ?? []);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  // Filter templates
  const filteredTemplates = useMemo(() => {
    if (!templates) return [];
    return templates.filter((template) => {
      // Search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matches =
          template.templateName.toLowerCase().includes(query) ||
          template.displayName.toLowerCase().includes(query) ||
          template.description.toLowerCase().includes(query) ||
          template.category.toLowerCase().includes(query);

        if (!matches) return false;
      }

      // Category filter
      if (selectedCategory && template.category !== selectedCategory) {
        return false;
      }

      // Compliance frameworks filter
      if (selectedFrameworks.length > 0) {
        const hasFramework = selectedFrameworks.some((framework) =>
          template.complianceFrameworks.includes(framework)
        );
        if (!hasFramework) return false;
      }

      return true;
    });
  }, [templates, searchQuery, selectedCategory, selectedFrameworks]);

  // Group templates by category
  const templatesByCategory = useMemo(() => {
    const grouped: Record<string, RoleTemplate[]> = {};

    filteredTemplates.forEach((template) => {
      if (!grouped[template.category]) {
        grouped[template.category] = [];
      }
      grouped[template.category]!.push(template);
    });

    return grouped;
  }, [filteredTemplates]);

  // Handle preview
  const handlePreview = (template: RoleTemplate) => {
    setPreviewTemplate(template);
    setRoleNameOverride('');
    setShowPreview(true);
  };

  // Handle instantiate
  const handleInstantiate = async (template: RoleTemplate) => {
    setInstantiating(template.templateId);

    try {
      const input: InstantiateTemplateInput = {
        templateId: template.templateId,
        tenantId,
        roleNameOverride: roleNameOverride || undefined,
      };

      const role = await repository.instantiateTemplate(input);

      // Reload templates to update usage count
      await loadTemplates();

      // Close modal
      setShowPreview(false);
      setPreviewTemplate(null);
      setRoleNameOverride('');

      // Notify parent
      if (onTemplateInstantiate) {
        onTemplateInstantiate(role.roleId);
      }
    } catch (err) {
      setError(err as Error);
    } finally {
      setInstantiating(null);
    }
  };

  // Get category icon
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'healthcare':
        return <IconShield size={20} />;
      case 'financial':
        return <IconTrophy size={20} />;
      case 'operational':
        return <IconUsers size={20} />;
      case 'compliance':
        return <IconCheck size={20} />;
      default:
        return <IconTemplate size={20} />;
    }
  };

  // Get category color
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'healthcare':
        return 'blue';
      case 'financial':
        return 'green';
      case 'operational':
        return 'orange';
      case 'compliance':
        return 'red';
      default:
        return 'gray';
    }
  };

  return (
    <Box>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <IconTemplate size={24} />
            <Title order={3}>Role Template Library</Title>
          </Group>

          <Group>
            <Tooltip label="Refresh templates">
              <ActionIcon
                variant="light"
                onClick={loadTemplates}
                disabled={loading}
              >
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>

            <Badge size="lg" variant="light">
              {filteredTemplates.length} templates
            </Badge>
          </Group>
        </Group>

        {/* Search and Filters */}
        <Card shadow="sm" padding="md" radius="md">
          <Grid>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <TextInput
                placeholder="Search templates..."
                leftSection={<IconSearch size={16} />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.currentTarget.value)}
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 4 }}>
              <Select
                placeholder="All categories"
                leftSection={<IconFilter size={16} />}
                value={selectedCategory}
                onChange={setSelectedCategory}
                data={[
                  { value: 'healthcare', label: 'Healthcare' },
                  { value: 'financial', label: 'Financial' },
                  { value: 'operational', label: 'Operational' },
                  { value: 'compliance', label: 'Compliance' },
                  { value: 'custom', label: 'Custom' },
                ]}
                clearable
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 4 }}>
              <MultiSelect
                placeholder="Filter by compliance"
                data={repository.getComplianceFrameworks()}
                value={selectedFrameworks}
                onChange={setSelectedFrameworks}
                clearable
              />
            </Grid.Col>
          </Grid>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            onClose={() => setError(null)}
            withCloseButton
          >
            {error.message}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Center p="xl">
            <Loader size="lg" />
          </Center>
        )}

        {/* Templates Grid */}
        {!loading && (
          <ScrollArea style={{ height: 'calc(100vh - 350px)' }}>
            <Stack gap="xl">
              {Object.entries(templatesByCategory).map(
                ([category, categoryTemplates]) => (
                  <Box key={category}>
                    <Group mb="md">
                      {getCategoryIcon(category)}
                      <Title order={4} style={{ textTransform: 'capitalize' }}>
                        {category}
                      </Title>
                      <Badge color={getCategoryColor(category)}>
                        {categoryTemplates.length}
                      </Badge>
                    </Group>

                    <Grid>
                      {categoryTemplates.map((template) => (
                        <Grid.Col
                          key={template.templateId}
                          span={{ base: 12, md: 6, lg: 4 }}
                        >
                          <TemplateCard
                            template={template}
                            onPreview={handlePreview}
                            getCategoryColor={getCategoryColor}
                          />
                        </Grid.Col>
                      ))}
                    </Grid>
                  </Box>
                )
              )}

              {/* Empty State */}
              {Object.keys(templatesByCategory).length === 0 && (
                <Paper p="xl" radius="md" withBorder>
                  <Stack ta="center" gap="md">
                    <IconTemplate size={48} style={{ opacity: 0.3 }} />
                    <Text size="lg" c="dimmed">
                      No templates found
                    </Text>
                    <Text size="sm" c="dimmed">
                      Try adjusting your search or filters
                    </Text>
                  </Stack>
                </Paper>
              )}
            </Stack>
          </ScrollArea>
        )}
      </Stack>

      {/* Preview Modal */}
      <Modal
        opened={showPreview}
        onClose={() => setShowPreview(false)}
        title="Template Preview"
        size="xl"
      >
        {previewTemplate && (
          <TemplatePreview
            template={previewTemplate}
            roleNameOverride={roleNameOverride}
            onRoleNameChange={setRoleNameOverride}
            onInstantiate={handleInstantiate}
            instantiating={instantiating === previewTemplate.templateId}
          />
        )}
      </Modal>
    </Box>
  );
}

// ============================================================================
// Template Card Component
// ============================================================================

interface TemplateCardProps {
  template: RoleTemplate;
  onPreview: (template: RoleTemplate) => void;
  getCategoryColor: (category: string) => string;
}

function TemplateCard({ template, onPreview, getCategoryColor }: TemplateCardProps) {
  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="sm">
        {/* Header */}
        <Group justify="space-between">
          <Badge color={getCategoryColor(template.category)} variant="light">
            {template.category}
          </Badge>
          <Badge variant="outline">v{template.version}</Badge>
        </Group>

        {/* Title */}
        <Title order={5}>{template.displayName}</Title>

        {/* Description */}
        <Text size="sm" c="dimmed" lineClamp={3}>
          {template.description}
        </Text>

        {/* Role Name */}
        <Group gap="xs">
          <Text size="xs" c="dimmed">
            Role:
          </Text>
          <Code>{template.roleDefinition.roleName}</Code>
        </Group>

        {/* Permissions Summary */}
        <Group gap="md">
          <Tooltip label="Scopes">
            <Badge size="sm" variant="light" color="blue">
              {template.roleDefinition.scopes.length} scopes
            </Badge>
          </Tooltip>
          <Tooltip label="Capabilities">
            <Badge size="sm" variant="light" color="green">
              {template.roleDefinition.capabilities.length} capabilities
            </Badge>
          </Tooltip>
        </Group>

        {/* Compliance Frameworks */}
        {template.complianceFrameworks.length > 0 && (
          <Group gap="xs">
            {template.complianceFrameworks.slice(0, 3).map((framework) => (
              <Badge key={framework} size="xs" color="red" variant="outline">
                {framework}
              </Badge>
            ))}
            {template.complianceFrameworks.length > 3 && (
              <Badge size="xs" color="red" variant="outline">
                +{template.complianceFrameworks.length - 3}
              </Badge>
            )}
          </Group>
        )}

        {/* Usage Count */}
        <Divider />

        <Group justify="space-between">
          <Group gap="xs">
            <IconUsers size={14} style={{ opacity: 0.5 }} />
            <Text size="xs" c="dimmed">
              Used {template.usageCount} times
            </Text>
          </Group>

          <Group gap="xs">
            <Button
              size="xs"
              variant="light"
              leftSection={<IconEye size={14} />}
              onClick={() => onPreview(template)}
            >
              Preview
            </Button>
          </Group>
        </Group>
      </Stack>
    </Card>
  );
}

// ============================================================================
// Template Preview Component
// ============================================================================

interface TemplatePreviewProps {
  template: RoleTemplate;
  roleNameOverride: string;
  onRoleNameChange: (value: string) => void;
  onInstantiate: (template: RoleTemplate) => void;
  instantiating: boolean;
}

function TemplatePreview({
  template,
  roleNameOverride,
  onRoleNameChange,
  onInstantiate,
  instantiating,
}: TemplatePreviewProps) {
  return (
    <Stack gap="md">
      {/* Template Info */}
      <Box>
        <Group justify="space-between" mb="sm">
          <Title order={4}>{template.displayName}</Title>
          <Badge>{template.category}</Badge>
        </Group>
        <Text size="sm" c="dimmed">
          {template.description}
        </Text>
      </Box>

      <Divider />

      {/* Role Definition */}
      <Box>
        <Text fw={500} mb="sm">
          Role Definition
        </Text>

        <Stack gap="xs">
          <Group>
            <Text size="sm" c="dimmed" style={{ width: 120 }}>
              Role Name:
            </Text>
            <Code>{template.roleDefinition.roleName}</Code>
          </Group>

          <Group>
            <Text size="sm" c="dimmed" style={{ width: 120 }}>
              Display Name:
            </Text>
            <Text size="sm">{template.roleDefinition.displayName}</Text>
          </Group>

          {template.roleDefinition.description && (
            <Group ta="left">
              <Text size="sm" c="dimmed" style={{ width: 120 }}>
                Description:
              </Text>
              <Text size="sm" style={{ flex: 1 }}>
                {template.roleDefinition.description}
              </Text>
            </Group>
          )}
        </Stack>
      </Box>

      {/* Scopes */}
      <Box>
        <Text fw={500} mb="sm">
          Scopes ({template.roleDefinition.scopes.length})
        </Text>
        <Group gap="xs">
          {template.roleDefinition.scopes.map((scope) => (
            <Badge key={scope} color="blue" variant="light">
              {scope}
            </Badge>
          ))}
        </Group>
      </Box>

      {/* Capabilities */}
      <Box>
        <Text fw={500} mb="sm">
          Capabilities ({template.roleDefinition.capabilities.length})
        </Text>
        <Group gap="xs">
          {template.roleDefinition.capabilities.map((capability) => (
            <Badge key={capability} color="green" variant="light">
              {capability}
            </Badge>
          ))}
        </Group>
      </Box>

      {/* Inheritance */}
      {template.roleDefinition.inheritsFrom.length > 0 && (
        <Box>
          <Text fw={500} mb="sm">
            Inherits From
          </Text>
          <Group gap="xs">
            {template.roleDefinition.inheritsFrom.map((parent) => (
              <Badge key={parent} color="orange" variant="light">
                {parent}
              </Badge>
            ))}
          </Group>
        </Box>
      )}

      {/* Compliance Frameworks */}
      {template.complianceFrameworks.length > 0 && (
        <Box>
          <Text fw={500} mb="sm">
            Compliance Frameworks
          </Text>
          <Group gap="xs">
            {template.complianceFrameworks.map((framework) => (
              <Badge key={framework} color="red" variant="outline">
                {framework}
              </Badge>
            ))}
          </Group>
        </Box>
      )}

      <Divider />

      {/* Instantiation Options */}
      <Box>
        <Text fw={500} mb="sm">
          Instantiation Options
        </Text>

        <TextInput
          label="Role Name Override (optional)"
          placeholder={template.roleDefinition.roleName}
          value={roleNameOverride}
          onChange={(e) => onRoleNameChange(e.currentTarget.value)}
          description="Leave empty to use default role name from template"
        />
      </Box>

      {/* Actions */}
      <Group justify="flex-end">
        <Button
          leftSection={<IconRocket size={16} />}
          onClick={() => onInstantiate(template)}
          loading={instantiating}
        >
          Instantiate Role
        </Button>
      </Group>

      {/* Metadata */}
      <Divider />
      <Group gap="xl">
        <Box>
          <Text size="xs" c="dimmed">
            Created By
          </Text>
          <Text size="sm">{template.createdBy}</Text>
        </Box>
        <Box>
          <Text size="xs" c="dimmed">
            Created At
          </Text>
          <Text size="sm">
            {new Date(template.createdAt).toLocaleString()}
          </Text>
        </Box>
        <Box>
          <Text size="xs" c="dimmed">
            Usage Count
          </Text>
          <Text size="sm">{template.usageCount} times</Text>
        </Box>
      </Group>
    </Stack>
  );
}
